import React, { useEffect, useRef, useState } from 'react';
import '@/assets/styles/components/whiteboard-question-gallery.styles.scss';
import ClassNames from 'classnames';

interface GalleryProps {
	images: string[];
}

const Gallery: React.FC<GalleryProps> = ({ images }) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [modalOpen, setModalOpen] = useState(false);

	const handlePrev = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		setActiveIndex(prev => (prev - 1 + images?.length) % images?.length);
	};

	const handleNext = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		setActiveIndex(prev => (prev + 1) % images?.length);
	};

	useEffect(() => {
		if (images?.length < 1) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setModalOpen(false);
			if (e.key === 'ArrowLeft') handlePrev();
			if (e.key === 'ArrowRight') handleNext();
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [modalOpen, images?.length]);

	const touchStartX = useRef<number | null>(null);
	const touchEndX = useRef<number | null>(null);

	const handleTouchStart = (e: React.TouchEvent) => {
		touchStartX.current = e.touches[0].clientX;
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		touchEndX.current = e.touches[0].clientX;
	};

	const handleTouchEnd = () => {
		if (touchStartX.current !== null && touchEndX.current !== null) {
			const distance = touchStartX.current - touchEndX.current;
			const minSwipeDistance = 50;
			if (distance > minSwipeDistance) {
				handleNext();
			} else if (distance < -minSwipeDistance) {
				handlePrev();
			}
		}
		touchStartX.current = null;
		touchEndX.current = null;
	};

	if (!images) {
		return null;
	}

	return (
		<div className="gallery">
			<div
				className="gallery__thumbnail"
				onClick={() => setModalOpen(true)}>
				<div className={'gallery__thumbnail-images-wrapper'}>
					{images?.map((img: string, index) => {
						return (
							<div className={'gallery__thumbnail-image-wrapper'}>
								<img
									className={ClassNames('gallery__thumbnail-image', {
										active: activeIndex === index
									})}
									src={img}
									alt={`Gallery image ${activeIndex + 1}`}
								/>
								<div className="zoom-icon">
									<i className={'icons-magnifer-zoom-in'} />
								</div>
							</div>
						);
					})}
				</div>

				<div
					className={ClassNames('gallery__arrows', {
						disabled: images?.length <= 0
					})}>
					<button
						onClick={e => {
							e.stopPropagation();
							handlePrev(e);
						}}>
						<i className={'icons-alt-arrow-left'} />
					</button>
					<button
						onClick={e => {
							e.stopPropagation();
							handleNext(e);
						}}>
						<i className={'icons-alt-arrow-right'} />
					</button>
				</div>
				<div className={'gallery__thumbnail-current-tab'}>
					{images?.map((img: string, index) => {
						return (
							<div
								className={ClassNames('current-tab-dot', {
									active: activeIndex === index
								})}
							/>
						);
					})}
				</div>
			</div>
			{modalOpen && (
				<div
					className="gallery__modal"
					onClick={() => setModalOpen(false)}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}>
					<button
						className="gallery__modal-close"
						onClick={() => setModalOpen(false)}>
						<i
							className={'icons-close'}
							style={{ fontSize: 30, color: '#fff' }}
						/>
					</button>
					<div
						className="gallery__modal-content"
						onClick={e => e.stopPropagation()}>
						<img
							src={images[activeIndex]}
							alt={`Gallery image ${activeIndex + 1}`}
						/>
						<div
							className={ClassNames('gallery__arrows', {
								disabled: images?.length <= 1
							})}>
							<button
								className="gallery__modal-arrow left"
								onClick={handlePrev}>
								<i className={'icons-alt-arrow-left'} />
							</button>
							<button
								className="gallery__modal-arrow right"
								onClick={handleNext}>
								<i className={'icons-alt-arrow-right'} />
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Gallery;
